import { Controller } from "@hotwired/stimulus"

// Makes a table row (or any container) behave like a link.
// Clicking anywhere on the row navigates to the URL, unless the user clicked
// an interactive element inside (link, button, input, label, select,
// textarea) — so inline edit controls keep working. Also supports keyboard
// activation via Enter / Space.
//
// Usage:
//   <tr data-controller="row-link"
//       data-row-link-url-value="/events/123"
//       data-action="click->row-link#navigate keydown.enter->row-link#navigate keydown.space->row-link#navigate"
//       role="link"
//       tabindex="0">
//     ...
//   </tr>
export default class extends Controller<HTMLElement> {
  static values = { url: String }

  declare urlValue: string

  connect() {
    this.element.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.onKeydown)
  }

  onKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return
    if (this.isInteractive(event.target as HTMLElement | null)) return

    event.preventDefault()
    this.visit()
  }

  navigate(event: MouseEvent) {
    if (this.isInteractive(event.target as HTMLElement | null)) return

    // Allow users to open in a new tab via cmd/ctrl/middle click.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return

    event.preventDefault()
    this.visit()
  }

  private visit() {
    if (!this.urlValue) return

    const turbo = (
      window as unknown as {
        Turbo?: { visit?: (url: string) => void }
      }
    ).Turbo

    if (!turbo?.visit) {
      window.location.href = this.urlValue
      return
    }

    turbo.visit(this.urlValue)
  }

  private isInteractive(target: HTMLElement | null): boolean {
    if (!target) return false
    return Boolean(
      target.closest(
        "a, button, input, select, textarea, label, [role='button'], [data-no-row-link]"
      )
    )
  }
}
